import { BudgetDto, BudgetLineDto } from 'api/budgets/models/BudgetDto';
import { useTranslation } from 'react-i18next';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import InfoIcon from 'common/components/infoIcon/InfoIcon';
import { Label } from 'common/components/texts/Texts';
import InputError from 'common/components/inputError/InputError';
import { OptionTitleSubTitle, SearchableSelectTitleSubTitleOption } from 'common/components/searchableSelectInput/optionsFormats/OptionTitleSubTitle';
import { SelectInputController, SelectInputOption } from 'common/components/selectInput/SelectInput';
import TextInput, { TextInputController } from 'common/components/textInput/TextInput';
import { formatBudgetVersion } from '../utils/BudgetUtils';
import BudgetsService from 'api/budgets/BudgetsService';
import Loading from 'common/services/Loading';
import Toast from 'common/services/Toast';
import Logger from 'common/services/Logger';
import { LOGGER_LOG_TYPE, MAX_DECIMAL_PLACES, MIN_DECIMAL_PLACES } from 'Config';
import { useDispatch, useSelector } from 'react-redux';
import { calculateAllLinesTotals, updateAllLines, updateBudget, updateLine, updateLinesExpressionsValues } from 'store/budget/action';
import { isTopLevelElementLine } from 'store/budget/reducer';
import ExpressionSystem from 'common/components/expressionButton/ExpressionSystem';
import { Reducers } from 'store/types';
import YesNoModal from 'common/components/modal/yesNoModal/YesNoModal';
import { useEffect, useMemo, useRef, useState } from 'react';
import DateInput, { DateInputController } from 'common/components/dateInput/DateInput';
import { TaxDto } from 'api/taxes/models/TaxDto';
import { BudgetStatus } from 'api/budgets/enums/BudgetStatus';
import { BudgetType } from 'api/budgets/enums/BudgetType';
import MoneyInput, { MoneyInputController } from 'common/components/moneyInput/MoneyInput';
import BudgetFileModelsService from 'api/budgetFileModels/BudgetFileModelsService';
import styles from './BudgetDetails.module.scss'
import { BudgetFieldType } from 'api/budgetsFields/enums/BudgetFieldType';
import { BudgetGeneralityDefaultDto } from 'api/budgetGeneralityDefaults/models/BudgetGeneralityDefaultDto';
import { pickGeneralityDesignation } from '../generalities/BudgetGeneralities';

interface Props {
    externalParts: SearchableSelectTitleSubTitleOption[];
    workTypes: SelectInputOption[];
    works: SelectInputOption[];
    workContacts: SearchableSelectTitleSubTitleOption[];
    taxes: TaxDto[];
    budgetFileModels: SelectInputOption[];
    languages: SelectInputOption[];
    languageIsoById: Record<string, string>;
    generalityDefaults: BudgetGeneralityDefaultDto[];
    type: 'create' | 'details' | 'edit' | 'version' | undefined;
    onChangeExternalPart: (value: string | undefined | null) => void;
    getWorkContacts: (value: string | undefined | null) => void;
    onChangeWorkType: () => void;
    onChangeOriginBudget: (value: string | undefined | null) => void;
    originBudgets: SelectInputOption[];
}

function BudgetScreen({
    type, externalParts, works, workContacts, workTypes, taxes, budgetFileModels, languages, languageIsoById, generalityDefaults,
    onChangeExternalPart, getWorkContacts, onChangeWorkType, onChangeOriginBudget, originBudgets
}: Props): JSX.Element | null {
    const { t, i18n } = useTranslation();
    const form = useFormContext<BudgetDto>();
    const dispatch = useDispatch();
    const alreadyHadWorkId = useRef(form.getValues('workId'));
    const alreadyHadWorkTypeId = useRef(form.getValues('workTypeId'));
    const taxesOptions = taxes.map(t => ({ selectedLabel: t.value?.toString() + ' %', label: t.description, value: t.id }));
    const statusesOptions = useMemo(() => Object.keys(BudgetStatus).map(key => ({
        label: t('budgets.status.' + key),
        value: key,
    })), [i18n.language]);
    const typesOptions = useMemo(() => Object.keys(BudgetType).map(key => ({
        label: t('budgets.budget_type.' + key),
        value: key,
    })), [i18n.language]);
    const { fields: fieldsValues } = useFieldArray({
        control: form.control,
        name: 'fieldsValues',
        keyName: 'key',
    });

    const lines = useSelector<Reducers, BudgetLineDto[]>(state => state.budget.lines);
    const [showTaxModal, setShowTaxModal] = useState(false);
    const [pendingTax, setPendingTax] = useState<{ id: string | null; value: number | null } | null>(null);

    const isDetails = type === 'details' || type === 'version';

    const errors = form.formState.errors;

    const onClickNewWork = () => {
        window.open('/business/works/create');
    }

    const onClickDetailsWork = (workdId: string) => {
        window.open('/business/works/details/' + workdId);
    }

    const retranslateGeneralities = (newLanguageId: string | null | undefined) => {
        if (newLanguageId) {
            form.clearErrors('languageId');
        }
        const newIso = newLanguageId ? languageIsoById[newLanguageId] : undefined;
        if (!newIso) return;
        const defaultsById = new Map(generalityDefaults.map(d => [d.id, d]));
        const generalities = form.getValues('generalities') ?? [];
        generalities.forEach((g, idx) => {
            if (!g.sourceGeneralityDefaultId) return; // linha manual → não tocar
            const def = defaultsById.get(g.sourceGeneralityDefaultId);
            if (!def) return;
            // "original" = a descrição ainda corresponde a uma das traduções do template.
            const isOriginal = (def.translations ?? []).some(tr => tr.designation === (g.designation ?? ''));
            if (!isOriginal) return; // utilizador já editou → não tocar
            form.setValue(`generalities.${idx}.designation`, pickGeneralityDesignation(def.translations, newIso));
        });
    }

    const onChangeWork = async (workId: string | null | undefined) => {
        const isWorkChange = !!alreadyHadWorkId.current && alreadyHadWorkId.current !== workId;

        if (isWorkChange) {
            form.setValue('managerContactId', '');
            form.setValue('onSiteContactId', '');
        }

        getWorkContacts(workId);

        if (!workId) {
            return;
        } else {
            form.clearErrors('workId');
        }
        try {
            Loading.show();

            const isWorkChange = !alreadyHadWorkId.current || alreadyHadWorkId.current !== workId;

            if (isWorkChange) {
                const workData = await BudgetsService.getWorkData(workId);
                const discounts = (workData.externalParts.filter(x => x.defaultDiscount).map(x => x.defaultDiscount) as number[]);
                const highestDiscount = discounts.length > 0 ? Math.max(...discounts) : null;
                const margins = (workData.externalParts.filter(x => x.defaultMarginPercentage).map(x => x.defaultMarginPercentage) as number[]);
                const highestMargin = margins.length > 0 ? Math.max(...margins) : null;

                // Only inherit the work's language when the budget language hasn't
                // been chosen yet — never override a language the user already set.
                const shouldInheritLanguage = !form.getValues('languageId');
                const newLanguageId = shouldInheritLanguage
                    ? workData.languageId ?? null
                    : form.getValues('languageId');

                dispatch(updateBudget({
                    defaultDiscountPercentage: highestDiscount,
                    defaultMarginPercentage: highestMargin,
                    languageId: newLanguageId,
                }));
                dispatch(updateLinesExpressionsValues());
                dispatch(calculateAllLinesTotals());

                form.setValue('defaultDiscountPercentage', highestDiscount);
                form.setValue('defaultMarginPercentage', highestMargin);
                if (shouldInheritLanguage) {
                    form.setValue('languageId', newLanguageId);
                    retranslateGeneralities(newLanguageId);
                }
            }

            alreadyHadWorkId.current = workId;
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get work data', error);
            Toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
        }
    }

    const onChangeTax = (taxId: string | null | undefined) => {
        const tax = taxes.find(x => x.id === taxId);
        const newTax = { id: tax ? tax.id : null, value: tax ? tax.value ?? null : null };

        dispatch(updateBudget({
            defaultTaxId: newTax.id,
            defaultTaxValue: newTax.value,
        }));

        if (lines.length > 0) {
            setPendingTax(newTax);
            setShowTaxModal(true);
        }
    }

    const onChangeStatus = (status: string | null | undefined) => {
        // Ao marcar o orçamento como ganho, preencher automaticamente a data
        // do ganho (só se ainda estiver vazia, para não sobrepor uma data manual).
        if (status === BudgetStatus.ACCEPTED && !form.getValues('wonDate')) {
            form.setValue('wonDate', new Date());
        }
    }

    const onCloseTaxModal = (applyToExisting: boolean) => {
        setShowTaxModal(false);
        if (applyToExisting && pendingTax) {
            dispatch(updateAllLines({
                taxId: pendingTax.id,
                taxValue: pendingTax.value ?? 0,
            }));
            dispatch(calculateAllLinesTotals());
        }
        setPendingTax(null);
    }

    const onChangeFileModel = async (fileModelId: string | null | undefined) => {
        if (!fileModelId) {
            return;
        }
        const fileModel = budgetFileModels.find(x => x.value === fileModelId);
        if (!fileModel) {
            return;
        }
        try {
            const terms = await BudgetFileModelsService.getModelPaymentTerms(fileModelId);
            const current = form.getValues('paymentTermsTranslations') ?? [];
            const updated = [...current];
            let changed = false;
            for (const t of terms ?? []) {
                if (!t.languageIso || !t.content) continue;
                const idx = updated.findIndex(x => x.languageIso === t.languageIso);
                if (idx >= 0) {
                    if (!updated[idx].content?.length) {
                        updated[idx] = { ...updated[idx], content: t.content };
                        changed = true;
                    }
                } else {
                    updated.push({ languageIso: t.languageIso, content: t.content });
                    changed = true;
                }
            }
            if (changed) {
                form.setValue('paymentTermsTranslations', updated);
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Failed to get payment terms', error);
        }
    }

    const onBlurDecimalPlaces = (v: number | undefined | null) => {
        dispatch(updateBudget({
            decimalPlaces: v ?? 0,
        }));
        dispatch(calculateAllLinesTotals());
    }

    const applyDefaultExpressionsToExistingLines = (field: 'discount' | 'margin' | 'decennialInsurance') => {
        const fieldMap = {
            discount: { budgetKey: 'defaultDiscountPercentage', exprField: 'discountPercentageExpression' as keyof BudgetLineDto, valueField: 'discountPercentage' as keyof BudgetLineDto },
            margin: { budgetKey: 'defaultCombinedMarginPercentage', exprField: 'marginPercentageExpression' as keyof BudgetLineDto, valueField: 'marginPercentage' as keyof BudgetLineDto },
            decennialInsurance: { budgetKey: 'defaultDecennialInsurancePercentage', exprField: 'decennialInsurancePercentageExpression' as keyof BudgetLineDto, valueField: 'decennialInsurancePercentage' as keyof BudgetLineDto },
        };
        const { budgetKey, exprField, valueField } = fieldMap[field];
        const exprId = ExpressionSystem.formatParameterId(['budget', budgetKey]);
        const expr = ExpressionSystem.serializeParameterMentionId(exprId);

        for (const line of lines) {
            if (isTopLevelElementLine(line, lines) && !line[exprField] && line[valueField] === 0) {
                dispatch(updateLine({ key: line.key, [exprField]: expr }));
            }
        }
    }

    const onBlurDefaultDiscountPercentage = (v: number | undefined | null) => {
        dispatch(updateBudget({
            defaultDiscountPercentage: v ?? 0,
        }));
        applyDefaultExpressionsToExistingLines('discount');
        dispatch(updateLinesExpressionsValues());
        dispatch(calculateAllLinesTotals());
    }

    const onBlurDefaultMarginPercentage = (v: number | undefined | null) => {
        dispatch(updateBudget({
            defaultMarginPercentage: v ?? 0,
        }));
        applyDefaultExpressionsToExistingLines('margin');
        dispatch(updateLinesExpressionsValues());
        dispatch(calculateAllLinesTotals());
    }

    const onBlurDefaultCommissionMarginPercentage = (v: number | undefined | null) => {
        dispatch(updateBudget({
            defaultCommissionMarginPercentage: v ?? 0,
        }));
        dispatch(updateLinesExpressionsValues());
        dispatch(calculateAllLinesTotals());
    }

    const onBlurDefaultDecennialInsurancePercentage = (v: number | undefined | null) => {
        dispatch(updateBudget({
            defaultDecennialInsurancePercentage: v ?? 0,
        }));
        applyDefaultExpressionsToExistingLines('decennialInsurance');
        dispatch(updateLinesExpressionsValues());
        dispatch(calculateAllLinesTotals());
    }

    useEffect(() => {
        if (budgetFileModels && budgetFileModels.length === 1) {
            const fileModelId = budgetFileModels[0].value;
            form.setValue('budgetFileModelId', fileModelId);
            onChangeFileModel(fileModelId)
        }
    }, [budgetFileModels]);

    useEffect(() => {
        onChangeWork(form.getValues('workId'));
    }, []);

    const watchedMargin = form.watch('defaultMarginPercentage');
    const watchedCommissionMargin = form.watch('defaultCommissionMarginPercentage');
    const combinedMargin = (watchedMargin ?? 0) + (watchedCommissionMargin ?? 0);

    return (
        <div className={styles.container}>
            <Row>
                {type !== 'create' && (
                    <>
                        <Col sm={12} md={3} xl={2} className="mb-3">
                            <Label space>{t('budgets.budget.number')}</Label>
                            <TextInputController
                                name='number'
                                control={form.control}
                                placeholder={t('budgets.budget.number')}
                                disabled={true}
                                rules={{ required: true, maxLength: 200 }}
                                hasError={Boolean(errors.number)}
                            />
                            <InputError error={errors.number} maxLength={200} />
                        </Col>
                        <Col sm={12} md={3} xl={2} className="mb-3">
                            <Label space>{t('budgets.budget.version')}</Label>
                            <TextInput
                                value={formatBudgetVersion(form.watch('displayVersion'))}
                                disabled={true}
                                readOnly
                            />
                        </Col>
                    </>
                )}
                <Col sm={12} md={3} xl={3} className="mb-3">
                    <Label space>{t('budgets.budget.type')}{!isDetails ? '*' : ''}</Label>
                    <SelectInputController
                        name='type'
                        control={form.control}
                        options={typesOptions}
                        disabled={isDetails || type === 'edit'}
                        onChange={() => onChangeWorkType()}
                        rules={{ required: true }}
                        hasError={Boolean(errors.type)}
                    />
                    <InputError error={errors.type} />
                </Col>
                {form.watch('type') === BudgetType.TS && (
                    <Col sm={12} md={3} xl={5} className="mb-3">
                        <Label space>{t('budgets.budget.origin_budget')}{!isDetails ? '*' : ''}</Label>
                        <SelectInputController
                            name='originBudgetId'
                            control={form.control}
                            options={originBudgets}
                            disabled={isDetails || type === 'edit'}
                            onChange={(value) => onChangeOriginBudget(value)}
                            rules={{ required: true }}
                            hasError={Boolean(errors.originBudgetId)}
                        />
                        <InputError error={errors.originBudgetId} />
                    </Col>
                )}
                <Col sm={12} className="mb-3">
                    <Label space>{t('budgets.budget.description')}{!isDetails ? '*' : ''}</Label>
                    <TextInputController
                        name='description'
                        control={form.control}
                        placeholder={t('budgets.budget.description')}
                        disabled={isDetails}
                        rules={{ required: true, maxLength: 500 }}
                        hasError={Boolean(errors.description)}
                    />
                    <InputError error={errors.description} maxLength={500} />
                </Col>
                <Col sm={12} md={6} className="mb-3">
                    <Label space>{t('budgets.budget.external_part')}</Label>
                    <SelectInputController
                        name='externalPartId'
                        control={form.control}
                        rules={{ required: false }}
                        hasError={Boolean(errors.externalPartId)}
                        options={externalParts}
                        formatOptionLabel={OptionTitleSubTitle}
                        onChange={onChangeExternalPart}
                        disabled={isDetails}
                        smallerValueContainer
                    />
                    <InputError error={errors.externalPartId} />
                </Col>
                <Col sm={12} md={6} className="mb-3">
                    <Label space>{t('budgets.budget.work')}{!isDetails ? '*' : ''}</Label>
                    <SelectInputController
                        name='workId'
                        control={form.control}
                        rules={{ required: true }}
                        hasError={Boolean(errors.workId)}
                        disabled={isDetails}
                        options={works}
                        smallerValueContainer
                        formatOptionLabel={OptionTitleSubTitle}
                        onClickAddNew={onClickNewWork}
                        onClickGoToDetails={onClickDetailsWork}
                        onChange={onChangeWork}
                    />
                    <InputError error={errors.workId} />
                </Col>
                <Col sm={12} md={6} className="mb-3">
                    <Label space>{t('budgets.budget.works_types')}{!isDetails ? '*' : ''}</Label>
                    <SelectInputController
                        name='workTypeId'
                        control={form.control}
                        options={workTypes}
                        disabled={isDetails || (type === 'edit' && Boolean(alreadyHadWorkTypeId.current))}
                        onChange={(workTypeId) => {
                            if (workTypeId) {
                                form.clearErrors('workTypeId');
                            }
                            onChangeWorkType();
                        }}
                        rules={{ required: true }}
                        hasError={Boolean(errors.workTypeId)}
                    />
                    <InputError error={errors.workTypeId} />
                </Col>
                <Col sm={12} md={6} className="mb-3">
                    <Label space>
                        {t('budgets.budget.language')}{!isDetails ? '*' : ''}
                        <OverlayTrigger placement="top" overlay={<Tooltip>{t('budgets.budget.language_tooltip')}</Tooltip>}>
                            <span><InfoIcon /></span>
                        </OverlayTrigger>
                    </Label>
                    <SelectInputController
                        name='languageId'
                        control={form.control}
                        options={languages}
                        disabled={isDetails}
                        rules={{ required: true }}
                        hasError={Boolean(errors.languageId)}
                        onChange={(newId) => {
                            // Keep the Redux budget in sync. saveBudget spreads the Redux
                            // budget over the form values, so without this the manually-picked
                            // language gets clobbered on create (the work's languageId leaks
                            // into Redux via onChangeWork).
                            dispatch(updateBudget({ languageId: newId ?? null }));
                            retranslateGeneralities(newId);
                        }}
                    />
                    <InputError error={errors.languageId} />
                </Col>
                <Col sm={12} md={6} className="mb-3">
                    <Label space>{t('budgets.budget.date')}</Label>
                    <DateInputController
                        name='date'
                        control={form.control}
                        disabled={isDetails}
                    />
                </Col>
                <Col sm={12} md={6} className="mb-3">
                    <Label space>{t('budgets.budget.validation_date')}</Label>
                    <DateInputController
                        name='validationDate'
                        control={form.control}
                        disabled={isDetails}
                    />
                </Col>
                <Col sm={12} md={6} className="mb-3">
                    <Label space>
                        {t('budgets.budget.default_remise')}
                    </Label>
                    <MoneyInputController
                        name='defaultDiscountPercentage'
                        control={form.control}
                        unitPrefix="%"
                        disabled={isDetails}
                        onBlur={onBlurDefaultDiscountPercentage}
                    />
                </Col>
                <Col sm={12} md={6} className="mb-3">
                    <Label space>
                        {t('budgets.budget.default_margin')}
                    </Label>
                    <MoneyInputController
                        name='defaultMarginPercentage'
                        control={form.control}
                        unitPrefix="%"
                        disabled={isDetails}
                        onBlur={onBlurDefaultMarginPercentage}
                    />
                </Col>
                <Col sm={12} md={6} className="mb-3">
                    <Label space>
                        {t('budgets.budget.default_commission_margin')}
                    </Label>
                    <MoneyInputController
                        name='defaultCommissionMarginPercentage'
                        control={form.control}
                        unitPrefix="%"
                        disabled={isDetails}
                        onBlur={onBlurDefaultCommissionMarginPercentage}
                    />
                </Col>
                <Col sm={12} md={6} className="mb-3">
                    <Label space>
                        {t('budgets.budget.default_combined_margin')}
                    </Label>
                    <MoneyInput
                        value={combinedMargin}
                        unitPrefix="%"
                        disabled={true}
                    />
                </Col>
                <Col sm={12} md={6} className="mb-3">
                    <Label space>
                        {t('budgets.budget.default_decennial_insurance')}
                    </Label>
                    <MoneyInputController
                        name='defaultDecennialInsurancePercentage'
                        control={form.control}
                        unitPrefix="%"
                        disabled={isDetails}
                        onBlur={onBlurDefaultDecennialInsurancePercentage}
                    />
                </Col>
                <Col sm={12} md={6} className="mb-3">
                    <Label space>{t('budgets.budget.default_tax')}</Label>
                    <SelectInputController
                        name='defaultTaxId'
                        control={form.control}
                        options={taxesOptions}
                        disabled={isDetails}
                        onChange={onChangeTax}
                    />
                </Col>
                <Col sm={12} md={6} className="mb-3">
                    <Label space>{t('budgets.budget.on_site_contact')}</Label>
                    <SelectInputController
                        name='onSiteContactId'
                        control={form.control}
                        rules={{ required: false }}
                        hasError={Boolean(errors.onSiteContactId)}
                        disabled={(!form.watch('workId') && type === 'create') || isDetails}
                        options={workContacts}
                        smallerValueContainer
                        formatOptionLabel={OptionTitleSubTitle}
                    />
                </Col>
                <Col sm={12} md={6} className="mb-3">
                    <Label space>{t('budgets.budget.manager_contact')}</Label>
                    <SelectInputController
                        name='managerContactId'
                        control={form.control}
                        rules={{ required: false }}
                        hasError={Boolean(errors.managerContactId)}
                        disabled={(!form.watch('workId') && type === 'create') || isDetails}
                        options={workContacts}
                        smallerValueContainer
                        formatOptionLabel={OptionTitleSubTitle}
                    />
                </Col>
                <Col sm={12} md={6} className="mb-3">
                    <Label space>{t('budgets.budget.budget_file_model')}</Label>
                    <SelectInputController
                        name='budgetFileModelId'
                        control={form.control}
                        options={budgetFileModels}
                        disabled={isDetails}
                        onChange={onChangeFileModel}
                    />
                </Col>
                <Col sm={12} md={6} className="mb-3">
                    <Label space>{t('budgets.budget.decimal_places')}</Label>
                    <MoneyInputController
                        name="decimalPlaces"
                        disabled={isDetails}
                        control={form.control}
                        decimalScale={0}
                        hideUnitsDrop
                        onBlur={onBlurDecimalPlaces}
                        isAllowed={({ floatValue }) => {
                            return (
                                !!floatValue && (floatValue >= MIN_DECIMAL_PLACES && floatValue <= MAX_DECIMAL_PLACES)
                            );
                        }}
                    />
                </Col>
                <Col sm={12} md={6} className="mb-3">
                    <Label space>{t('budgets.budget.status')}</Label>
                    <SelectInputController
                        name='status'
                        control={form.control}
                        options={statusesOptions}
                        disabled={isDetails}
                        removeRemoveButton
                        onChange={onChangeStatus}
                    />
                </Col>
                <Col sm={12} md={6} className="mb-3">
                    <Label space>{t('budgets.budget.won_date')}</Label>
                    <DateInputController
                        name='wonDate'
                        control={form.control}
                        disabled={isDetails}
                    />
                </Col>
                {fieldsValues.map((fv, index) => (
                    <Col sm={12} md={6} key={fv.key} className="mb-3">
                        <Label space>{fv.name}{!isDetails && fv.isRequired ? '*' : ''}</Label>
                        {fv.fieldType === BudgetFieldType.SELECT && (
                            <SelectInputController
                                name={`fieldsValues.${index}.budgetFieldOptionId`}
                                control={form.control}
                                options={fv.options.map(op => ({ label: op.label, value: op.id! }))}
                                disabled={isDetails}
                                rules={{ required: fv.isRequired }}
                                hasError={Boolean(errors.fieldsValues ? errors.fieldsValues[index]?.budgetFieldOptionId : undefined)}
                            />)}
                        {fv.fieldType === BudgetFieldType.TEXT && (
                            <TextInputController
                                name={`fieldsValues.${index}.value`}
                                control={form.control}
                                disabled={isDetails}
                                rules={{ required: fv.isRequired }}
                                hasError={Boolean(errors.fieldsValues ? errors.fieldsValues[index]?.value : undefined)}
                                placeholder={t('common.write_here')}
                            />)}
                        {fv.fieldType === BudgetFieldType.NUMBER && (
                            <MoneyInputController
                                name={`fieldsValues.${index}.value`}
                                control={form.control}
                                disabled={isDetails}
                                rules={{ required: fv.isRequired }}
                                hasError={Boolean(errors.fieldsValues ? errors.fieldsValues[index]?.value : undefined)}
                                hideUnitsDrop
                            />)}
                        {fv.fieldType === BudgetFieldType.DATE && (
                            <Controller
                                name={`fieldsValues.${index}.value`}
                                control={form.control}
                                rules={{ required: fv.isRequired }}
                                render={({ field }) => (
                                    <DateInput
                                        value={field.value ? new Date(field.value) : null}
                                        disabled={isDetails}
                                        onChange={(d) => field.onChange(d ? d.toISOString() : null)}
                                    />
                                )}
                            />)}
                        {fv.fieldType === BudgetFieldType.SELECT && <InputError error={errors.fieldsValues ? errors.fieldsValues[index]?.budgetFieldOptionId : undefined} />}
                        {fv.fieldType !== BudgetFieldType.SELECT && <InputError error={errors.fieldsValues ? errors.fieldsValues[index]?.value : undefined} />}
                    </Col>
                ))}
            </Row>

            <YesNoModal
                title={t('budgets.budget.apply_tax_modal_title')}
                message={t('budgets.budget.apply_tax_modal_message')}
                isOpen={showTaxModal}
                onClose={onCloseTaxModal}
            />
        </div>
    );
}

export default BudgetScreen;
